import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { generateJobSpec, type InclusiveLanguageHint } from '../../api/ai';
import {
  createJobDraft,
  updateJobDraft,
  fetchJobDraft,
  getStoredJobDraft,
  submitJobDraft,
  type JobDraftPayload,
  type JobDraft,
} from '../../api/jobDrafts';
import type { OccupationCatalogRole } from '../../api/occupations';
import { useOccupationCatalog, useOccupationSearch } from '../../hooks/useOccupations';
import { useAuth } from '../../hooks/useAuth';

const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Temporary'];

const LOCAL_INCLUSIVE_RULES: Array<{
  pattern: RegExp;
  phrase: string;
  suggestion: string;
  category: InclusiveLanguageHint['category'];
  severity: InclusiveLanguageHint['severity'];
}> = [
  {
    pattern: /\brockstar\b|\bninja\b|\bguru\b/,
    phrase: 'Avoid hero terms',
    suggestion: 'Focus on outcomes and skills instead of labels like "rockstar" or "ninja".',
    category: 'corporate',
    severity: 'warning',
  },
  {
    pattern: /\bnative (english )?speaker\b|\benglish native\b/,
    phrase: 'Native speaker',
    suggestion: 'Ask for strong written and verbal communication instead of "native speaker".',
    category: 'corporate',
    severity: 'warning',
  },
  {
    pattern: /\bfast-paced\b|\bwork hard play hard\b/,
    phrase: 'Fast-paced culture',
    suggestion: 'Describe tempo clearly (e.g., ambitious roadmap with team support) instead of "fast-paced".',
    category: 'ableist',
    severity: 'info',
  },
  {
    pattern: /\byoung\b|\bdigital native\b/,
    phrase: 'Age-coded language',
    suggestion: 'Avoid age-coded terms; outline tools, tech, and expectations instead.',
    category: 'corporate',
    severity: 'warning',
  },
  {
    pattern: /\bhe\/she\b|\bguys\b/,
    phrase: 'Gendered language',
    suggestion: 'Use neutral language (they/them or role titles) to keep the spec inclusive.',
    category: 'gendered',
    severity: 'warning',
  },
];

const dedupeHints = (hints: InclusiveLanguageHint[]) => {
  const seen = new Set<string>();
  return hints.filter((hint) => {
    const key = `${hint.category}:${hint.phrase.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildLocalInclusiveHints = (summary: string, responsibilities: string, requirements: string): InclusiveLanguageHint[] => {
  const combined = [summary, responsibilities, requirements].filter(Boolean).join('\n').toLowerCase();
  if (!combined) return [];
  const matches = LOCAL_INCLUSIVE_RULES.filter((rule) => rule.pattern.test(combined)).map((rule) => ({
    phrase: rule.phrase,
    suggestion: rule.suggestion,
    category: rule.category,
    severity: rule.severity,
  }));
  return dedupeHints(matches);
};

const parseMultiLineText = (value: string) =>
  value
    .split(/\n+/)
    .map((item) => item.replace(/^[\s•*\-–—\d.\s]+/, '').trim())
    .filter(Boolean);

type JobFormState = {
  title: string;
  location: string;
  type: string;
  salary: string;
  summary: string;
  responsibilities: string;
  requirements: string;
  occupationId?: string;
};

const initialState: JobFormState = {
  title: '',
  location: '',
  type: 'Full-time',
  salary: '',
  summary: '',
  responsibilities: '',
  requirements: '',
  occupationId: undefined,
};

export function JobWizard() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<JobFormState>(initialState);
  const [generating, setGenerating] = useState(false);
  const [aiTone, setAiTone] = useState<'inclusive' | 'bold'>('inclusive');
  const [aiHints, setAiHints] = useState<InclusiveLanguageHint[]>([]);
  const [localHints, setLocalHints] = useState<InclusiveLanguageHint[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedIso, setLastSavedIso] = useState<string | null>(null);
  const [hydratingDraft, setHydratingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const occupationCatalog = useOccupationCatalog(form.title);
  const occupationSearch = useOccupationSearch(form.title);
  const { user } = useAuth();
  const employerId = user?.sub ?? '';
  const employerName = user?.name ?? user?.email ?? 'Employer';

  const handleChange = (field: keyof JobFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      occupationId: field === 'title' ? undefined : prev.occupationId,
    }));
    if (field === 'summary' || field === 'responsibilities' || field === 'requirements') {
      setAiHints([]);
    }
  };

  const handleSelectRole = (role: OccupationCatalogRole) => {
    setForm((prev) => ({
      ...prev,
      title: role.title,
      occupationId: role.id,
    }));
  };

  const persistDraft = useCallback(
    async (payload: JobDraftPayload) => {
      const hasContent = Object.values(payload).some((value) => (value ? String(value).trim().length > 0 : false));
      if (!hasContent && !draftId) {
        setAutosaveStatus('idle');
        return;
      }
      try {
        setAutosaveStatus('saving');
        const draft = draftId ? await updateJobDraft(draftId, payload) : await createJobDraft(payload);
        if (!draftId) {
          setDraftId(draft.id);
        }
        setLastSavedIso(draft.updatedAt);
        setAutosaveStatus('saved');
      } catch (error) {
        console.error(error);
        setAutosaveStatus('error');
      }
    },
    [draftId],
  );

  const applyDraftToForm = (draft: JobDraft) => {
    setForm((prev) => ({
      ...prev,
      title: draft.title ?? '',
      location: draft.location ?? '',
      type: draft.type ?? prev.type,
      salary: draft.salary ?? '',
      summary: draft.summary ?? '',
      responsibilities: draft.responsibilities ?? '',
      requirements: draft.requirements ?? '',
      occupationId: draft.occupationId,
    }));
  };

  const { title, location, type, salary, summary, responsibilities, requirements, occupationId } = form;
  const allHints = useMemo(() => dedupeHints([...localHints, ...aiHints]), [localHints, aiHints]);
  const catalogRoleSuggestions = useMemo(() => {
    if (!occupationCatalog.data?.roles) return [];
    return occupationCatalog.data.roles.slice(0, 8);
  }, [occupationCatalog.data?.roles]);
  const searchRoleSuggestions = useMemo(() => {
    if (!occupationSearch.data) return [];
    return occupationSearch.data.slice(0, 6);
  }, [occupationSearch.data]);

  useEffect(() => {
    const nextHints = buildLocalInclusiveHints(summary, responsibilities, requirements);
    setLocalHints((current) => {
      if (
        current.length === nextHints.length &&
        current.every(
          (hint, index) =>
            hint.phrase === nextHints[index]?.phrase &&
            hint.suggestion === nextHints[index]?.suggestion &&
            hint.category === nextHints[index]?.category &&
            hint.severity === nextHints[index]?.severity,
        )
      ) {
        return current;
      }
      return nextHints;
    });
  }, [summary, responsibilities, requirements]);

  useEffect(() => {
    const payload: JobDraftPayload = {
      title,
      location,
      type,
      salary,
      summary,
      responsibilities,
      requirements,
      occupationId,
      occupationTitle: occupationId ? title : undefined,
    };
    const timer = setTimeout(() => {
      void persistDraft(payload);
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    title,
    location,
    type,
    salary,
    summary,
    responsibilities,
    requirements,
    occupationId,
    persistDraft,
  ]);

  useEffect(() => {
    const storedDraft = getStoredJobDraft();
    if (!storedDraft) {
      return;
    }

    let cancelled = false;
    setHydratingDraft(true);
    setDraftId(storedDraft.id);
    applyDraftToForm(storedDraft);
    setLastSavedIso(storedDraft.updatedAt);
    setAutosaveStatus('saved');

    const hydrate = async () => {
      try {
        const latest = await fetchJobDraft(storedDraft.id);
        if (!latest || cancelled) return;
        setDraftId(latest.id);
        applyDraftToForm(latest);
        setLastSavedIso(latest.updatedAt);
        setAutosaveStatus('saved');
      } catch (error) {
        console.warn('[job-wizard] Unable to hydrate draft', error);
        if (!cancelled) {
          setAutosaveStatus('error');
        }
      } finally {
        if (!cancelled) {
          setHydratingDraft(false);
        }
      }
    };

    void hydrate();

    return () => {
      cancelled = true;
    };
  }, []);

  const toneOptions = [
    { value: 'inclusive' as const, label: 'Inclusive tone' },
    { value: 'bold' as const, label: 'Bold tone' },
  ];

  const formattedLastSaved = lastSavedIso
    ? new Date(lastSavedIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  const autosaveMessage =
    autosaveStatus === 'saving'
      ? 'Saving draft…'
      : autosaveStatus === 'error'
      ? 'Autosave failed. Changes are stored locally.'
      : autosaveStatus === 'saved' && formattedLastSaved
      ? `Saved ${formattedLastSaved}`
      : 'Autosave ready';

  const renderInclusiveHints = () =>
    allHints.length === 0 ? null : (
      <div className="rounded-xl border border-[#d2ac47]/40 bg-[#ba9036]/10 p-4 text-sm text-gray-100">
        <p className="text-xs uppercase tracking-[0.3em] text-[#f7ef8a]">Inclusive language</p>
        <ul className="mt-3 space-y-2">
          {allHints.map((hint, index) => (
            <li key={`${hint.phrase}-${hint.category}-${index}`} className="space-y-2 rounded-lg bg-black/10 p-3">
              <p className="font-semibold text-white">"{hint.phrase || 'Suggestion'}"</p>
              <p className="text-gray-200">{hint.suggestion}</p>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-black/30 text-xs uppercase tracking-wider text-[#f7ef8a]">{hint.category}</Badge>
                <Badge variant="outline" className="border-white/20 text-xs uppercase tracking-wider text-white">
                  {hint.severity === 'warning' ? 'Review' : 'Heads up'}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );

  const handleGenerate = async () => {
    if (!form.title) {
      toast.error('Add a job title so Lighthouse AI has context.');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        role: form.title,
        teamFocus: form.summary || undefined,
        summary: form.summary || undefined,
        location: form.location || undefined,
        type: form.type || undefined,
        salary: form.salary || undefined,
        responsibilities: parseMultiLineText(form.responsibilities),
        requirements: parseMultiLineText(form.requirements),
        tone: aiTone,
        occupationId: occupationId || undefined,
        occupationTitle: form.title || undefined,
      };
      const response = await generateJobSpec(payload);
      setForm((prev) => ({
        ...prev,
        summary: response.summary,
        responsibilities: response.responsibilities,
        requirements: response.requirements,
      }));
      setAiHints(dedupeHints(response.inclusiveHints ?? []));
      toast.success('AI draft ready. Tweak before publishing.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to generate draft right now.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!draftId) {
      toast.error('Autosave must complete at least once before submitting.');
      return;
    }
    if (!employerId) {
      toast.error('Sign in again to submit this role.');
      return;
    }
    if (!title.trim() || !location.trim()) {
      toast.error('Add a job title and location before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await submitJobDraft(draftId, {
        employerId,
        employerName,
        title,
        location,
        type,
        salary,
        summary,
        responsibilities,
        requirements,
        occupationId,
        occupationTitle: occupationId ? title : undefined,
      });
      toast.success('Draft submitted. Expect a moderation decision within a few hours.');
      setStep(1);
      setForm(initialState);
      setAiHints([]);
      setLocalHints([]);
      setDraftId(null);
      setAutosaveStatus('idle');
      setLastSavedIso(null);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to submit draft right now.');
    } finally {
      setSubmitting(false);
    }
  };

  if (hydratingDraft) {
    return (
      <main className="min-h-screen bg-[#080a1a] pb-24 text-white" aria-label="Loading job wizard">
        <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
          <p className="text-sm uppercase tracking-[0.4em] text-[#f7ef8a]">Loading</p>
          <p className="mt-4 text-2xl font-semibold text-white">Retrieving your AI job draft…</p>
          <p className="mt-2 text-gray-400">
            Hold tight while we pull the latest autosave from Lighthouse. This keeps your notes synced across devices.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080a1a] pb-24" aria-labelledby="job-wizard-title">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <Badge className="bg-gold-gradient text-black shadow-[#d2ac47]/35">Step {step} of 3</Badge>
            <h1 id="job-wizard-title" className="mt-4 text-3xl font-semibold text-white">Create a job specification</h1>
            <p className="text-sm text-gray-400">Give Lighthouse a strong brief. Verified roles go live within hours.</p>
            <p className={`text-xs ${autosaveStatus === 'error' ? 'text-red-300' : 'text-gray-500'}`}>{autosaveMessage}</p>
          </div>
        </div>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-lg text-white">
              {step === 1 && 'Role basics'}
              {step === 2 && 'Responsibilities & requirements'}
              {step === 3 && 'Review & publish'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-6 pt-0">
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-title">Job title</label>
                  <Input id="job-title" value={form.title} onChange={handleChange('title')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="e.g. Principal Product Manager" />
                  {occupationId ? (
                    <p className="text-xs text-[#ba9036]">Linked to occupation: {occupationId}</p>
                  ) : null}
                  {searchRoleSuggestions.length > 0 && (
                    <div className="mt-2 space-y-2 rounded-lg border border-white/10 bg-black/40 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-gray-400">Matching occupations</p>
                      <div className="flex flex-wrap gap-2">
                        {searchRoleSuggestions.map((role) => (
                          <Button
                            key={role.id}
                            type="button"
                            variant="outline"
                            className="border-white/20 bg-white/5 text-sm text-white hover:bg-white/10"
                            onClick={() =>
                              handleSelectRole({
                                id: role.id,
                                title: role.preferredLabel,
                                aliases: role.altLabels,
                                categoryId: '',
                                categoryLabel: 'ESCO',
                                region: 'uk',
                                group: role.conceptUri ?? '',
                              })
                            }
                          >
                            {role.preferredLabel}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  {catalogRoleSuggestions.length > 0 && (
                    <div className="space-y-2 rounded-lg border border-white/10 bg-black/40 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-gray-400">Common UK roles</p>
                      <div className="flex flex-wrap gap-2">
                        {catalogRoleSuggestions.map((role) => (
                          <Button
                            key={role.id}
                            type="button"
                            variant="outline"
                            className="border-white/20 bg-white/5 text-sm text-white hover:bg-white/10"
                            onClick={() => handleSelectRole(role)}
                          >
                            {role.title}
                            <span className="ml-2 text-[11px] text-gray-400">{role.categoryLabel}</span>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-location">Location</label>
                  <Input id="job-location" value={form.location} onChange={handleChange('location')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="Hybrid · London" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-type">Job type</label>
                  <div className="flex flex-wrap gap-2">
                    {jobTypes.map((type) => (
                      <Button
                        key={type}
                        type="button"
                        variant={form.type === type ? 'default' : 'outline'}
                        className={form.type === type ? 'bg-gold-gradient text-black hover:brightness-110' : 'border-white/20 text-white'}
                        onClick={() => setForm((prev) => ({ ...prev, type }))}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-salary">Salary band</label>
                  <Input id="job-salary" value={form.salary} onChange={handleChange('salary')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="£85,000 – £100,000 + benefits" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-summary">Overview</label>
                  <Textarea id="job-summary" rows={4} value={form.summary} onChange={handleChange('summary')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="Describe why this role exists, the mission, and the outcomes you expect within six months." />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-responsibilities">Responsibilities</label>
                  <Textarea id="job-responsibilities" rows={5} value={form.responsibilities} onChange={handleChange('responsibilities')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="List key outcomes and what success looks like." />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300" htmlFor="job-requirements">Requirements</label>
                  <Textarea id="job-requirements" rows={5} value={form.requirements} onChange={handleChange('requirements')} className="border-white/10 bg-white/10 text-white placeholder:text-gray-400" placeholder="Skills, behaviours, and flexible arrangements you support." />
                </div>
                <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.035] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-white">AI assistance</p>
                      <p className="text-xs text-gray-400">Generate inclusive copy based on the brief above.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {toneOptions.map((tone) => (
                        <Button
                          key={tone.value}
                          type="button"
                          variant={aiTone === tone.value ? 'default' : 'outline'}
                          className={
                            aiTone === tone.value
                              ? 'bg-gold-gradient text-black hover:brightness-110'
                              : 'border-white/20 text-white hover:bg-white/10'
                          }
                          onClick={() => setAiTone(tone.value)}
                        >
                          {tone.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button
                    type="button"
                    disabled={generating}
                    onClick={handleGenerate}
                    className="w-full bg-gold-gradient text-black hover:brightness-110"
                  >
                    {generating ? 'Generating…' : 'Generate AI draft'}
                  </Button>
                  {renderInclusiveHints()}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 text-sm text-gray-300">
                <p>Review your draft. Lighthouse will run inclusion checks before moderation.</p>
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <p className="text-sm text-gray-400">Job title</p>
                  <p className="text-lg font-semibold text-white">{form.title || 'Untitled role'}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-300">
                    <Badge className="border-white/10 bg-white/[0.07] text-gray-100">{form.location || 'Location TBD'}</Badge>
                    <Badge className="border-white/10 bg-white/[0.07] text-gray-100">{form.type}</Badge>
                    {form.salary && (
                      <Badge className="border-white/10 bg-white/[0.07] text-gray-100">{form.salary}</Badge>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <p className="text-sm text-gray-400">Summary</p>
                  <p className="text-gray-200 whitespace-pre-line">{form.summary || 'Add a compelling summary before publishing.'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <p className="text-sm text-gray-400">Responsibilities</p>
                  <p className="text-gray-200 whitespace-pre-line">{form.responsibilities || 'Outline responsibilities to give candidates clarity.'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <p className="text-sm text-gray-400">Requirements</p>
                  <p className="text-gray-200 whitespace-pre-line">{form.requirements || 'List required skills and highlight flexibility or support.'}</p>
                </div>
                {renderInclusiveHints()}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => setStep((prev) => Math.max(1, prev - 1))} disabled={step === 1}>
                Back
              </Button>
              {step < 3 ? (
                <Button className="bg-gold-gradient text-black hover:brightness-110" onClick={() => setStep((prev) => prev + 1)}>
                  Continue
                </Button>
              ) : (
                <Button
                  className="bg-gold-gradient text-black hover:brightness-110"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Submit for moderation'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
